import React, { useState, useEffect } from "react";
import OTPInput from "./OTPInput";
import { CHAT_LOCK_PIN_LENGTH } from "../utils/chatLock";
import "../styles/ChatLock.css";

function ChatLockModal({
  mode,
  contactName,
  onCancel,
  onSubmit,
  error: externalError,
}) {
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [localError, setLocalError] = useState("");

  const isSetup = mode === "setup";
  const title = isSetup ? "Create chat lock PIN" : "Enter chat lock PIN";
  const description = isSetup
    ? "Set a 4-digit PIN. You will need it to open locked chats."
    : contactName
      ? `Enter your PIN to open the chat with ${contactName}.`
      : "Enter your PIN to open this locked chat.";

  const handleSubmit = () => {
    setLocalError("");
    if (pin.length !== CHAT_LOCK_PIN_LENGTH) {
      setLocalError(`Enter a ${CHAT_LOCK_PIN_LENGTH}-digit PIN`);
      return;
    }
    if (isSetup) {
      if (confirmPin.length !== CHAT_LOCK_PIN_LENGTH) {
        setLocalError("Confirm your PIN");
        return;
      }
      if (pin !== confirmPin) {
        setLocalError("PINs do not match");
        return;
      }
    }
    onSubmit(pin);
  };

  const error = externalError || localError;

  return (
    <div className="chat-lock-modal-backdrop" onClick={onCancel}>
      <div className="chat-lock-panel" onClick={(e) => e.stopPropagation()}>
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#2563eb"
          strokeWidth="2"
          style={{ marginBottom: 12 }}
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0110 0v4" />
        </svg>
        <h3>{title}</h3>
        <p>{description}</p>
        <OTPInput value={pin} onChange={setPin} length={CHAT_LOCK_PIN_LENGTH} masked />
        {isSetup && (
          <>
            <p style={{ marginTop: 16, marginBottom: 8, fontSize: "0.85rem" }}>
              Confirm PIN
            </p>
            <OTPInput
              value={confirmPin}
              onChange={setConfirmPin}
              length={CHAT_LOCK_PIN_LENGTH}
              masked
            />
          </>
        )}
        {error && <div className="chat-lock-error">{error}</div>}
        <div className="chat-lock-actions">
          <button type="button" className="chat-lock-btn secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="chat-lock-btn primary"
            onClick={handleSubmit}
            disabled={pin.length !== CHAT_LOCK_PIN_LENGTH}
          >
            {isSetup ? "Save PIN" : "Unlock"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ChatLockUnlockPanel({
  contactName,
  onUnlock,
  onCancel,
  onForgotPin,
  error,
  pinResetKey = 0,
}) {
  const [pin, setPin] = useState("");

  useEffect(() => {
    setPin("");
  }, [pinResetKey]);

  const handleUnlock = () => {
    if (pin.length !== CHAT_LOCK_PIN_LENGTH) return;
    onUnlock(pin);
  };

  return (
    <div className="chat-lock-overlay">
      <div className="chat-lock-panel">
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#2563eb"
          strokeWidth="2"
          style={{ marginBottom: 12 }}
        >
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0110 0v4" />
        </svg>
        <h3>Chat locked</h3>
        <p>
          {contactName
            ? `Enter your PIN to view messages with ${contactName}.`
            : "Enter your PIN to open this chat."}
        </p>
        <OTPInput value={pin} onChange={setPin} length={CHAT_LOCK_PIN_LENGTH} masked />
        {error && <div className="chat-lock-error">{error}</div>}
        <div className="chat-lock-actions">
          {onCancel && (
            <button type="button" className="chat-lock-btn secondary" onClick={onCancel}>
              Back
            </button>
          )}
          <button
            type="button"
            className="chat-lock-btn primary"
            onClick={handleUnlock}
            disabled={pin.length !== CHAT_LOCK_PIN_LENGTH}
          >
            Unlock
          </button>
        </div>
        {onForgotPin && (
          <button type="button" className="chat-lock-forgot-pin" onClick={onForgotPin}>
            Forgot PIN?
          </button>
        )}
      </div>
    </div>
  );
}

export default ChatLockModal;
